import { useEffect, useMemo, useState } from 'react';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { fmtINRFull, fmtDate } from '@/lib/format';
import {
  ArrowRight, ChevronLeft, Plus, Trash2, Save, CheckCircle2, AlertTriangle, Calculator, MapPin,
} from 'lucide-react';

/**
 * Iter41 — Extended Edit Work Order (two-step: full-form → Review → Confirm).
 *
 * Extends the existing WO edit path — no new module, no new collection.
 * New fields (financials, core_stages, visit_terms) are additive; if the
 * WO has never had them, the form shows "Not Yet Specified" and the user
 * can fill them in place.
 *
 * Guardrails:
 *   - Core stage percentages must total 100.00% before Review is enabled.
 *   - Users edit BASE and %. Derived ₹ is read-only (server recomputes).
 *   - Trip/visit fees are OUTSIDE the 100% core allocation.
 */
export function WOEditDialog({ woId, open, onClose, onSaved }) {
  const [step, setStep] = useState(1);           // 1: form, 2: review
  const [snap, setSnap] = useState(null);
  const [form, setForm] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const reset = () => { setStep(1); setSnap(null); setForm(null); setPreview(null); setError(''); setBusy(false); };
  const handleClose = () => { reset(); onClose?.(); };

  useEffect(() => {
    if (!open || !woId) return;
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/work_orders/${woId}/edit_snapshot`);
        if (!alive) return;
        setSnap(r.data);
        const wo = r.data.wo || {};
        setForm({
          wo_number: wo.wo_number || (wo.extraction || {}).wo_number || '',
          wo_date:   wo.wo_date || (wo.extraction || {}).wo_date_iso || '',
          scope_summary: (wo.extraction || {}).scope_summary || '',
          notes: wo.notes || '',
          financials: wo.financials || null,   // null → "Not Yet Specified"
          core_stages: wo.core_stages || [],
          visit_terms: wo.visit_terms || null,
        });
      } catch (e) { toast.error(parseErr(e)); handleClose(); }
    })();
    return () => { alive = false; };
  }, [open, woId]);

  // ------------------------------------------------------------------
  // Live derived amounts (mirror server logic for instant feedback)
  // ------------------------------------------------------------------
  const derivedFinancials = useMemo(() => {
    const f = form?.financials || {};
    const base = Number(f.base_value_inr) || 0;
    const pct  = Number(f.gst_percent) || 0;
    const treatment = f.gst_treatment || 'plus_gst';
    let baseEx = base, gstAmt = 0, total = base;
    if (treatment === 'plus_gst') { gstAmt = +(base * pct / 100).toFixed(2); total = +(base + gstAmt).toFixed(2); }
    else if (treatment === 'inclusive_of_gst') { baseEx = +(base / (1 + pct/100)).toFixed(2); gstAmt = +(base - baseEx).toFixed(2); total = base; }
    else { gstAmt = 0; total = base; }
    return { base, baseEx, gstAmt, total, treatment, pct };
  }, [form?.financials]);

  const runningTotal = useMemo(() => {
    return (form?.core_stages || []).reduce((s, r) => s + (Number(r.percentage) || 0), 0);
  }, [form?.core_stages]);
  const totalOK = Math.abs(runningTotal - 100) < 0.01;

  const stageDerived = (pct) => {
    const base = derivedFinancials.baseEx;
    const gp = derivedFinancials.pct;
    const b = +(base * (Number(pct) || 0) / 100).toFixed(2);
    const g = +(b * gp / 100).toFixed(2);
    return { base: b, gst: g, total: +(b + g).toFixed(2) };
  };

  // ------------------------------------------------------------------
  const setF = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const setFin = (k, v) => setForm(prev => ({
    ...prev,
    financials: { ...(prev.financials || {}), [k]: v },
  }));
  const setStage = (idx, k, v) => setForm(prev => ({
    ...prev,
    core_stages: prev.core_stages.map((s, i) => i === idx ? { ...s, [k]: v } : s),
  }));
  const addStage = () => setForm(prev => ({
    ...prev,
    core_stages: [...(prev.core_stages || []), { name: '', percentage: 0 }],
  }));
  const removeStage = (idx) => setForm(prev => ({
    ...prev,
    core_stages: prev.core_stages.filter((_, i) => i !== idx),
  }));
  const setVT = (k, v) => setForm(prev => ({
    ...prev,
    visit_terms: { ...(prev.visit_terms || {}), [k]: v, not_yet_specified: false },
  }));

  // Only send fields that were actually edited (server will merge)
  const buildPayload = () => {
    const p = {};
    // Header
    if (form.wo_number !== undefined) p.wo_number = form.wo_number;
    if (form.wo_date) p.wo_date = form.wo_date;
    if (form.scope_summary !== undefined) p.scope_summary = form.scope_summary;
    if (form.notes !== undefined) p.notes = form.notes;
    // Financials — only if user provided a base
    if (form.financials && form.financials.base_value_inr !== undefined
        && form.financials.base_value_inr !== null && form.financials.base_value_inr !== '') {
      p.financials = {
        base_value_inr: Number(form.financials.base_value_inr),
        gst_percent: form.financials.gst_percent != null ? Number(form.financials.gst_percent) : null,
        gst_treatment: form.financials.gst_treatment || 'plus_gst',
      };
    }
    // Stages
    if ((form.core_stages || []).length > 0) {
      p.core_stages = form.core_stages.filter(s => (s.name || '').trim()).map(s => ({
        id: s.id, name: s.name.trim(),
        percentage: Number(s.percentage) || 0,
        due_date: s.due_date || null,
        payment_status: s.payment_status || 'pending',
      }));
    }
    // Visit terms — only send if user typed anything real
    const vt = form.visit_terms || {};
    if (vt.included_visits != null || vt.additional_basis || vt.additional_fixed_fee != null
        || vt.travel_reimbursement || vt.incidental_reimbursement || vt.gst_on_additional
        || vt.notes || vt.additional_billed != null) {
      p.visit_terms = {
        not_yet_specified: false,
        included_visits: vt.included_visits != null ? Number(vt.included_visits) : null,
        additional_billed: vt.additional_billed ?? null,
        additional_basis: vt.additional_basis || null,
        additional_fixed_fee: vt.additional_fixed_fee != null ? Number(vt.additional_fixed_fee) : null,
        travel_reimbursement: vt.travel_reimbursement || null,
        incidental_reimbursement: vt.incidental_reimbursement || null,
        gst_on_additional: vt.gst_on_additional || null,
        notes: vt.notes || null,
      };
    }
    return p;
  };

  const goReview = async () => {
    setError('');
    if ((form.core_stages || []).length > 0 && !totalOK) {
      setError(`Core stage percentages must total exactly 100.0%. Currently at ${runningTotal.toFixed(1)}%.`);
      return;
    }
    setBusy(true);
    try {
      const payload = buildPayload();
      const r = await api.post(`/work_orders/${woId}/edit/preview`, payload);
      setPreview(r.data);
      setStep(2);
    } catch (e) { setError(parseErr(e)); }
    finally { setBusy(false); }
  };

  const confirmSave = async () => {
    setBusy(true);
    setError('');
    try {
      const payload = buildPayload();
      const r = await api.post(`/work_orders/${woId}/edit/confirm`, payload);
      toast.success('Work Order updated');
      // Iter47.1 — surface the MP-side auto-sync so it isn't silent.
      const sync = r.data?.mp_display_sync;
      if (sync) {
        const label = sync.project_name || 'linked Master Project';
        const from = sync.from ? `"${sync.from}"` : '(empty)';
        toast.success(
          `Also updated ${label}'s displayed WO number: ${from} → "${sync.to}"`,
          { duration: 5000 });
      }
      onSaved?.(r.data);
      reset();
    } catch (e) { setError(parseErr(e)); }
    finally { setBusy(false); }
  };

  // ------------------------------------------------------------------
  if (!open) return null;
  if (!snap || !form) {
    return (
      <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
        <DialogContent><DialogHeader><DialogTitle>Loading Work Order…</DialogTitle></DialogHeader></DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-[860px] max-h-[90vh] overflow-y-auto"
                     data-testid={`wo-edit-dialog-${woId}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator size={16} />
            {step === 1 ? 'Edit Work Order' : 'Review changes — Confirm to save'}
          </DialogTitle>
          <DialogDescription>
            {step === 1
              ? 'Update any field. Derived amounts recalculate automatically. Nothing is saved until you Confirm on the next step.'
              : 'Only the fields you changed are shown. Confirming will refresh linked Project totals, milestones, Collections, and reports.'}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4" data-testid="wo-edit-form">
            {/* HEADER */}
            <Section title="Header">
              <div className="grid grid-cols-2 gap-3">
                <FormField label="WO number"><Input value={form.wo_number || ''}
                       onChange={e => setF('wo_number', e.target.value)} data-testid="wo-edit-number" /></FormField>
                <FormField label="WO date"><Input type="date" value={form.wo_date || ''}
                       onChange={e => setF('wo_date', e.target.value)} data-testid="wo-edit-date" /></FormField>
                <FormField label="Scope summary" full><Textarea rows={2} value={form.scope_summary || ''}
                       onChange={e => setF('scope_summary', e.target.value)} data-testid="wo-edit-scope" /></FormField>
              </div>
            </Section>

            {/* FINANCIALS */}
            <Section title="Financials — used in every stage calc"
                     right={!snap.has_financials && <span className="chip">Not Yet Specified</span>}>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Base Value (ex-GST) ₹">
                  <Input type="number" min="0" step="0.01"
                         style={{ minWidth: 180 }}
                         value={form.financials?.base_value_inr ?? ''}
                         onChange={e => setFin('base_value_inr', e.target.value)}
                         placeholder="e.g. 10,00,000"
                         data-testid="wo-edit-base" />
                </FormField>
                <FormField label="GST %">
                  <Input type="number" min="0" max="50" step="0.1"
                         value={form.financials?.gst_percent ?? ''}
                         onChange={e => setFin('gst_percent', e.target.value)}
                         placeholder="18"
                         data-testid="wo-edit-gst" />
                </FormField>
                <FormField label="GST treatment">
                  <select className="h-9 w-full border rounded-md px-2 text-[14px] bg-[hsl(var(--card))]"
                          style={{ borderColor: 'hsl(var(--border))' }}
                          value={form.financials?.gst_treatment || 'plus_gst'}
                          onChange={e => setFin('gst_treatment', e.target.value)}
                          data-testid="wo-edit-gst-treatment">
                    <option value="plus_gst">Plus GST</option>
                    <option value="inclusive_of_gst">Inclusive of GST</option>
                    <option value="zero_rated">Zero-rated</option>
                    <option value="reverse_charge">Reverse charge</option>
                  </select>
                </FormField>
                <FormField label="Derived (auto)">
                  <div className="text-[12.5px] leading-tight tabular-nums pt-1">
                    <div>Base ex-GST: <b>{fmtINRFull(derivedFinancials.baseEx)}</b></div>
                    <div>GST:         <b>{fmtINRFull(derivedFinancials.gstAmt)}</b></div>
                    <div>Total (incl-GST): <b>{fmtINRFull(derivedFinancials.total)}</b></div>
                  </div>
                </FormField>
              </div>
            </Section>

            {/* CORE STAGES */}
            <Section title="Core stages — must total 100.0%"
                     right={<span className={`chip ${totalOK ? 'chip-success' : 'chip-warning'}`}
                                   data-testid="wo-edit-pct-total">
                       Running total {runningTotal.toFixed(1)}%
                     </span>}>
              <div className="overflow-x-auto">
                <table className="tbl w-full" data-testid="wo-edit-stages">
                  <thead>
                    <tr>
                      <th style={{ width: 30 }}>#</th>
                      <th>Stage name</th>
                      <th style={{ width: 90 }}>%</th>
                      <th className="text-right">Base ex-GST</th>
                      <th className="text-right">GST</th>
                      <th className="text-right">Total</th>
                      <th style={{ width: 140 }}>Due date</th>
                      <th style={{ width: 30 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(form.core_stages || []).map((s, i) => {
                      const d = stageDerived(s.percentage);
                      return (
                        <tr key={i} data-testid={`wo-edit-stage-row-${i}`}>
                          <td className="text-[12px] text-[hsl(var(--foreground-muted))]">{i + 1}</td>
                          <td><Input value={s.name || ''} onChange={e => setStage(i, 'name', e.target.value)}
                                     data-testid={`wo-edit-stage-name-${i}`} /></td>
                          <td><Input type="number" min="0" max="100" step="0.1"
                                     value={s.percentage ?? ''}
                                     onChange={e => setStage(i, 'percentage', e.target.value)}
                                     data-testid={`wo-edit-stage-pct-${i}`} /></td>
                          <td className="text-right tabular-nums text-[12.5px]">{fmtINRFull(d.base)}</td>
                          <td className="text-right tabular-nums text-[12.5px]">{fmtINRFull(d.gst)}</td>
                          <td className="text-right tabular-nums text-[12.5px] font-medium">{fmtINRFull(d.total)}</td>
                          <td><Input type="date" value={s.due_date || ''}
                                     onChange={e => setStage(i, 'due_date', e.target.value)} /></td>
                          <td><Button size="sm" variant="ghost" onClick={() => removeStage(i)}
                                       data-testid={`wo-edit-stage-remove-${i}`}><Trash2 size={12} /></Button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="mt-2 flex justify-between text-[12px]">
                <Button variant="outline" size="sm" onClick={addStage} data-testid="wo-edit-stage-add">
                  <Plus size={12} className="mr-1" />Add stage
                </Button>
                <span className={totalOK ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--warning))]'}>
                  {totalOK ? <><CheckCircle2 size={12} className="inline mr-1" />Adds to 100.0%. OK to review.</>
                           : <><AlertTriangle size={12} className="inline mr-1" />
                             {`Adjust ${(100 - runningTotal).toFixed(1)}% to hit 100.0% before Review.`}</>}
                </span>
              </div>
            </Section>

            {/* VISIT TERMS */}
            <Section title="Trip & Visit terms"
                     right={!snap.has_visit_terms && <span className="chip">Not Yet Specified</span>}>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Included visits">
                  <Input type="number" min="0" max="200"
                         value={form.visit_terms?.included_visits ?? ''}
                         onChange={e => setVT('included_visits', e.target.value)}
                         data-testid="wo-edit-vt-included" />
                </FormField>
                <FormField label="Additional visits billed?">
                  <select className="h-9 w-full border rounded-md px-2 text-[14px] bg-[hsl(var(--card))]"
                          style={{ borderColor: 'hsl(var(--border))' }}
                          value={form.visit_terms?.additional_billed === true ? 'yes'
                                : form.visit_terms?.additional_billed === false ? 'no' : ''}
                          onChange={e => setVT('additional_billed', e.target.value === 'yes')}
                          data-testid="wo-edit-vt-billed">
                    <option value="">— choose —</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </FormField>
                <FormField label="Additional basis">
                  <select className="h-9 w-full border rounded-md px-2 text-[14px] bg-[hsl(var(--card))]"
                          style={{ borderColor: 'hsl(var(--border))' }}
                          value={form.visit_terms?.additional_basis || ''}
                          onChange={e => setVT('additional_basis', e.target.value)}
                          data-testid="wo-edit-vt-basis">
                    <option value="">— choose —</option>
                    <option value="fixed_fee">Fixed fee</option>
                    <option value="actuals">Actuals</option>
                    <option value="fixed_plus_actuals">Fixed fee + actuals</option>
                  </select>
                </FormField>
                <FormField label="Additional fixed fee ₹">
                  <Input type="number" min="0" step="0.01"
                         value={form.visit_terms?.additional_fixed_fee ?? ''}
                         onChange={e => setVT('additional_fixed_fee', e.target.value)}
                         data-testid="wo-edit-vt-fixed-fee" />
                </FormField>
                <FormField label="Travel reimbursement">
                  <Input value={form.visit_terms?.travel_reimbursement || ''}
                         onChange={e => setVT('travel_reimbursement', e.target.value)}
                         placeholder="At actuals / slab / client bears / not reimbursed"
                         data-testid="wo-edit-vt-travel" />
                </FormField>
                <FormField label="Incidental reimbursement">
                  <Input value={form.visit_terms?.incidental_reimbursement || ''}
                         onChange={e => setVT('incidental_reimbursement', e.target.value)}
                         placeholder="At actuals / slab / client bears / not reimbursed"
                         data-testid="wo-edit-vt-incidental" />
                </FormField>
                <FormField label="GST on additional">
                  <select className="h-9 w-full border rounded-md px-2 text-[14px] bg-[hsl(var(--card))]"
                          style={{ borderColor: 'hsl(var(--border))' }}
                          value={form.visit_terms?.gst_on_additional || ''}
                          onChange={e => setVT('gst_on_additional', e.target.value)}
                          data-testid="wo-edit-vt-gst-add">
                    <option value="">— choose —</option>
                    <option value="same_as_wo">Same as WO</option>
                    <option value="18">18%</option>
                    <option value="12">12%</option>
                    <option value="5">5%</option>
                    <option value="0">0%</option>
                    <option value="exempt">Exempt</option>
                  </select>
                </FormField>
                <FormField label="Notes" full>
                  <Textarea rows={2} value={form.visit_terms?.notes || ''}
                            onChange={e => setVT('notes', e.target.value)}
                            data-testid="wo-edit-vt-notes" />
                </FormField>
              </div>
              <div className="mt-1 text-[11.5px] text-[hsl(var(--foreground-subtle))] flex items-center gap-1">
                <MapPin size={11} /> Trips, reimbursements and visits stay OUTSIDE the 100% core stage total.
              </div>
            </Section>
          </div>
        )}

        {step === 2 && preview && (
          <div className="space-y-4" data-testid="wo-review-body">
            <ChangeGroup title="Header changes" rows={preview.changed_header}
                          emptyMsg="No header changes." dtid="wo-review-header" />
            <ChangeGroup title="Financials changes" rows={preview.changed_financials}
                          emptyMsg="No financial changes." dtid="wo-review-financials" />
            {(preview.changed_stages || []).length > 0 && (
              <div className="border rounded-md p-3" style={{ borderColor: 'hsl(var(--border))' }}
                   data-testid="wo-review-stages">
                <div className="text-[12.5px] font-medium mb-2">Stage changes</div>
                <ul className="text-[12.5px] space-y-1">
                  {preview.changed_stages.map((c, i) => (
                    <li key={i}>· <b>{c.name || 'Row change'}</b>: {Object.entries(c.changes).map(([k, v]) =>
                        `${k}: ${v.before ?? '—'} → ${v.after ?? '—'}`).join('; ')}</li>
                  ))}
                </ul>
              </div>
            )}
            <ChangeGroup title="Trip & visit terms" rows={preview.changed_visit_terms}
                          emptyMsg="No visit-term changes." dtid="wo-review-visit-terms" />

            <div className="border rounded-md p-3 bg-[hsl(var(--surface-1))]"
                 style={{ borderColor: 'hsl(var(--border))' }}
                 data-testid="wo-review-cascade">
              <div className="text-[12.5px] font-medium mb-1">Records that will refresh on Confirm</div>
              <ul className="text-[12px] space-y-0.5">
                {preview.cascade?.linked_master_project && (
                  <li>· Master Project <b>{preview.cascade.linked_master_project.code}</b> — {preview.cascade.linked_master_project.project_name}</li>
                )}
                <li>· Milestones: {preview.cascade?.milestones_before} → {preview.cascade?.milestones_after}</li>
                <li>· Collections pending repricing: {preview.cascade?.collections_pending_repricing ?? 0}</li>
                <li>· Stage sign-offs: {preview.cascade?.stage_signoffs_impact}</li>
                <li>· Weekly PDF summary refreshes on next generate.</li>
              </ul>
            </div>

            {!preview.validation?.ok && (
              <div className="border rounded-md p-3 text-[12.5px] text-[hsl(var(--danger))]"
                   style={{ borderColor: 'hsl(var(--danger))', background: 'hsl(0 90% 97%)' }}
                   role="alert" data-testid="wo-review-validation-err">
                <AlertTriangle size={12} className="inline mr-1" />{preview.validation.message}
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="text-[12.5px] text-[hsl(var(--danger))] p-2 rounded border mt-2"
               style={{ borderColor: 'hsl(var(--danger))', background: 'hsl(0 90% 97%)' }}
               role="alert" data-testid="wo-edit-error">{error}</div>
        )}

        <DialogFooter>
          {step === 2 && (
            <Button variant="ghost" onClick={() => setStep(1)}
                    data-testid="wo-review-back">
              <ChevronLeft size={13} className="mr-1" />Back
            </Button>
          )}
          <Button variant="ghost" onClick={handleClose} disabled={busy}>Cancel</Button>
          {step === 1 && (
            <Button onClick={goReview}
                    disabled={busy || ((form.core_stages || []).length > 0 && !totalOK)}
                    data-testid="wo-edit-review-btn">
              Review changes <ArrowRight size={13} className="ml-1" />
            </Button>
          )}
          {step === 2 && (
            <Button onClick={confirmSave} disabled={busy || !preview?.validation?.ok}
                    data-testid="wo-edit-confirm-btn">
              <Save size={13} className="mr-1" />
              {busy ? 'Saving…' : 'Confirm Update'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function Section({ title, right, children }) {
  return (
    <div className="border rounded-md p-3" style={{ borderColor: 'hsl(var(--border))' }}>
      <div className="flex items-center justify-between mb-2">
        <div className="font-medium text-[13px]">{title}</div>
        <div>{right}</div>
      </div>
      {children}
    </div>
  );
}

function FormField({ label, children, full }) {
  return (
    <div className={full ? 'col-span-2' : ''}>
      <label className="text-[11px] text-[hsl(var(--foreground-muted))] uppercase tracking-wider block mb-1">{label}</label>
      {children}
    </div>
  );
}

function ChangeGroup({ title, rows, emptyMsg, dtid }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="border rounded-md p-3" style={{ borderColor: 'hsl(var(--border))' }} data-testid={dtid}>
      <div className="text-[12.5px] font-medium mb-1">{title}</div>
      <ul className="text-[12.5px] space-y-0.5">
        {rows.map((r, i) => (
          <li key={i}>· <b>{r.field}</b>: {r.before ?? '—'} → {r.after ?? '—'}</li>
        ))}
      </ul>
    </div>
  );
}
